import React, { useState, useEffect } from 'react';
import { Save, Calendar, Info, Plus, Trash2 } from 'lucide-react';
import { useContract } from '../../hooks/useContract';
import toast from 'react-hot-toast';

interface DailyLoginBonus {
  day: number;
  coins: number;
  experience: number;
  isSpecial?: boolean;
}

interface DailyLoginTabProps {
  inputClasses: string;
  currentWallet: string;
}

const DEFAULT_BONUSES: DailyLoginBonus[] = [
  { day: 1, coins: 100, experience: 50 },
  { day: 2, coins: 200, experience: 100 },
  { day: 3, coins: 300, experience: 150 },
  { day: 4, coins: 400, experience: 200 },
  { day: 5, coins: 500, experience: 250 },
  { day: 6, coins: 600, experience: 300 },
  { day: 7, coins: 1000, experience: 500, isSpecial: true },
  { day: 8, coins: 800, experience: 400 },
  { day: 9, coins: 900, experience: 450 },
  { day: 10, coins: 1000, experience: 500 },
  { day: 11, coins: 1100, experience: 550 },
  { day: 12, coins: 1200, experience: 600 },
  { day: 13, coins: 1300, experience: 650 },
  { day: 14, coins: 2500, experience: 1000, isSpecial: true }
];

export function DailyLoginTab({ inputClasses, currentWallet }: DailyLoginTabProps) {
  const { isDemoMode } = useContract();
  const [bonuses, setBonuses] = useState<DailyLoginBonus[]>(() => {
    const saved = localStorage.getItem(`dailyLogin_bonuses_${currentWallet}`);
    return saved ? JSON.parse(saved) : DEFAULT_BONUSES;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Vérifier que les jours sont consécutifs
      const sortedBonuses = [...bonuses].sort((a, b) => a.day - b.day);
      const isConsecutive = sortedBonuses.every((bonus, index) => 
        index === 0 || bonus.day === sortedBonuses[index - 1].day + 1
      );

      if (!isConsecutive) {
        toast.error('Les jours doivent être consécutifs');
        return;
      }

      // Sauvegarder la configuration
      localStorage.setItem(`dailyLogin_bonuses_${currentWallet}`, JSON.stringify(bonuses));
      toast.success(isDemoMode ? 'Configuration sauvegardée (Mode Démo)' : 'Configuration sauvegardée');
    } catch (error) {
      console.error('Error saving daily login config:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const addBonus = () => {
    const lastDay = Math.max(...bonuses.map(b => b.day), 0);
    setBonuses([...bonuses, {
      day: lastDay + 1,
      coins: 100,
      experience: 50
    }]);
  };

  const removeBonus = (day: number) => {
    setBonuses(bonuses.filter(b => b.day !== day));
  };

  const updateBonus = (day: number, updates: Partial<DailyLoginBonus>) => {
    setBonuses(bonuses.map(b => b.day === day ? { ...b, ...updates } : b));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {isDemoMode && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex items-center">
            <Info className="text-yellow-400 mr-2" size={20} />
            <p className="text-yellow-700">
              Mode démo actif : Les changements seront sauvegardés localement uniquement
            </p>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Calendar className="text-blue-500" />
          Bonus de Connexion Quotidiens
        </h3>

        <div className="space-y-4">
          {bonuses.map((bonus) => (
            <div 
              key={bonus.day}
              className={`grid grid-cols-5 gap-4 p-4 rounded-lg ${
                bonus.isSpecial 
                  ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200'
                  : 'bg-gray-50'
              }`}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jour
                </label>
                <input
                  type="number"
                  value={bonus.day}
                  onChange={(e) => updateBonus(bonus.day, { day: parseInt(e.target.value) })}
                  className={inputClasses}
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coins
                </label>
                <input
                  type="number"
                  value={bonus.coins}
                  onChange={(e) => updateBonus(bonus.day, { coins: parseInt(e.target.value) })}
                  className={inputClasses}
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expérience
                </label>
                <input
                  type="number"
                  value={bonus.experience}
                  onChange={(e) => updateBonus(bonus.day, { experience: parseInt(e.target.value) })}
                  className={inputClasses}
                  min="1"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bonus.isSpecial}
                    onChange={(e) => updateBonus(bonus.day, { isSpecial: e.target.checked })}
                    className="w-4 h-4 text-yellow-500 rounded border-gray-300 focus:ring-yellow-500"
                  />
                  <span className="text-sm text-gray-700">Bonus Spécial</span>
                </label>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => removeBonus(bonus.day)}
                  className="p-2 text-red-600 hover:text-red-700"
                  disabled={bonuses.length <= 1}
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addBonus}
          className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-700"
        >
          <Plus size={20} />
          Ajouter un bonus
        </button>
      </div>

      <div className="flex justify-end pt-4">
        <button
          type="submit"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
        >
          <Save size={20} />
          Sauvegarder la Configuration
        </button>
      </div>
    </form>
  );
}